import React, { useState } from 'react';
import { Edit2, Leaf, Trash2 } from 'lucide-react';
import type { Bed, MapMode, Plant, Zone } from '../../types';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { BedForm } from '../forms/BedForm';
import { PhotoTimeline } from '../PhotoTimeline';
import { NoteTimeline } from '../NoteTimeline';
import { STATUS_BG, STATUS_LABELS, SUN_LABELS, TYPE_EMOJI } from '../../lib/utils';

type Tab = 'plants' | 'photos' | 'notes' | 'info';

interface BedDetailProps {
  bed: Bed;
  zone: Zone | undefined;
  zones: Zone[];
  plants: Plant[];
  mapMode: MapMode;
  drawingPoints: [number, number][];
  onSetMapMode: (m: MapMode) => void;
  onClearDrawing: () => void;
  onUpdate: (data: Partial<Bed>) => Promise<void>;
  onDelete: () => void;
  onPlantClick: (plant: Plant) => void;
  onClose: () => void;
}

export function BedDetail({
  bed,
  zone,
  zones,
  plants,
  mapMode,
  drawingPoints,
  onSetMapMode,
  onClearDrawing,
  onUpdate,
  onDelete,
  onPlantClick,
  onClose,
}: BedDetailProps) {
  const [tab, setTab] = useState<Tab>('plants');
  const [editing, setEditing] = useState(false);

  const bedPlants = plants.filter((p) => p.bed_id === bed.id);

  return (
    <>
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        <div
          className="w-4 h-4 rounded-full mt-1 shrink-0 ring-2 ring-white shadow"
          style={{ background: zone?.color ?? '#4ade80' }}
        />
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug">{bed.name}</h2>
          {zone && <p className="text-sm text-gray-500 dark:text-gray-400">{zone.name}</p>}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="sm" aria-label="Edit bed" onClick={() => setEditing(true)}><Edit2 size={15} /></Button>
          <Button variant="ghost" size="sm" aria-label="Delete bed" onClick={onDelete}><Trash2 size={15} className="text-red-500" /></Button>
        </div>
      </div>

      {/* Meta badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        {bed.sun_exposure && (
          <Badge className="bg-amber-100 text-amber-700">{SUN_LABELS[bed.sun_exposure]}</Badge>
        )}
        <Badge className="bg-gray-100 text-gray-600">{bedPlants.length} plant{bedPlants.length !== 1 ? 's' : ''}</Badge>
      </div>

      {bed.description && (
        <p className="text-sm text-gray-600 mb-4">{bed.description}</p>
      )}

      {/* Tabs */}
      <div role="tablist" className="flex border-b border-gray-100 dark:border-gray-800 mb-4">
        {(['plants','photos','notes','info'] as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              tab === t
                ? 'border-garden-600 text-garden-700 dark:text-garden-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-label={tab}>
        {tab === 'plants' && (
          <div className="flex flex-col gap-1.5">
            {bedPlants.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
                <Leaf size={28} />
                <p className="text-sm">No plants in this bed</p>
              </div>
            ) : (
              bedPlants.map((plant) => (
                <button
                  key={plant.id}
                  onClick={() => onPlantClick(plant)}
                  className="flex items-center gap-3 text-left p-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                >
                  <span className="text-xl" aria-hidden="true">{TYPE_EMOJI[plant.plant_type]}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{plant.common_name}</p>
                    {plant.cultivar && <p className="text-xs text-gray-400 italic truncate">'{plant.cultivar}'</p>}
                  </div>
                  <Badge className={STATUS_BG[plant.status]}>{STATUS_LABELS[plant.status]}</Badge>
                </button>
              ))
            )}
          </div>
        )}

        {tab === 'photos' && <PhotoTimeline entityType="bed" entityId={bed.id} />}
        {tab === 'notes' && <NoteTimeline entityType="bed" entityId={bed.id} />}

        {tab === 'info' && (
          <div className="flex flex-col gap-3 text-sm">
            {bed.soil_notes && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">Soil</p>
                <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">{bed.soil_notes}</p>
              </div>
            )}
            {bed.irrigation_notes && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">Irrigation</p>
                <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">{bed.irrigation_notes}</p>
              </div>
            )}
            {!bed.soil_notes && !bed.irrigation_notes && (
              <p className="text-gray-400 text-center py-4">No additional info</p>
            )}
          </div>
        )}
      </div>

      {/* Edit modal */}
      <Modal open={editing} onClose={() => setEditing(false)} title="Edit Bed" size="lg">
        <BedForm
          initial={bed}
          zones={zones}
          mapMode={mapMode}
          drawingPoints={drawingPoints}
          onSetMapMode={onSetMapMode}
          onClearDrawing={onClearDrawing}
          onSubmit={async (data) => { await onUpdate(data); setEditing(false); }}
          onCancel={() => setEditing(false)}
          isEdit
        />
      </Modal>
    </>
  );
}
